import { Mention } from "@jugl-web/domain-resources/common/components/RichTextarea";
import { useMultipleUserProfiles } from "@jugl-web/domain-resources/users/hooks/useMultipleUserProfiles";
import { TicketDetailsModel } from "@jugl-web/rest-api/tickets/models/TicketDetails";
import { sortCollectionAlphabetically } from "@jugl-web/utils/array/sortCollectionAlphabetically";
import { useMemo } from "react";

export interface UseTicketMentionsOptions {
  entityId: string;
  meId: string;
  ticket: TicketDetailsModel;
}

/**
 * Builds the list of @mention candidates for a ticket comment input.
 * Includes the ticket creator and all assignees, excluding the current user.
 */
export const useTicketMentions = ({
  entityId,
  meId,
  ticket,
}: UseTicketMentionsOptions) => {
  const combinedUserIds = useMemo(() => {
    const set = new Set<string>([
      ticket.created_by.user_id,
      ...ticket.assignees.map((assignee) => assignee.user_id),
    ]);

    set.delete(meId);

    return Array.from(set);
  }, [meId, ticket.assignees, ticket.created_by.user_id]);

  const { profiles } = useMultipleUserProfiles({
    entityId,
    userIds: combinedUserIds,
  });

  const mentions = useMemo<Mention[]>(
    () =>
      sortCollectionAlphabetically(
        profiles.map<Mention>((profile) => ({
          userId: profile.id,
          name: profile.displayName,
          imageUrl: profile.avatar || null,
        })),
        (mention) => mention.name
      ),
    [profiles]
  );

  return { mentions };
};
